import { useState } from "react";
import { useAuth } from "@clerk/react";
import { Star, RefreshCw, MessageCircle, Mic, Music, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { makeApi, aiErrorText } from "@/lib/api";
import { useT } from "@/i18n";
import { AI_TEXT, EXAMPLES } from "@/components/ailab/content";
import useImageGen from "@/components/ailab/useImageGen";
import Studio, { studioField } from "@/components/ailab/Studio";

const SceneCard = ({ i, tx, idea }) => {
  const { lang } = useT();
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const { generate, loading } = useImageGen(tx.error, lang);
  const make = async () => setImg((await generate(`${idea}. ${text}. Storyboard scene ${i + 1}, kids cartoon style`)) || img);
  return (
    <div className="bg-white rounded-3xl p-3 shadow-md space-y-2" data-testid={`scene-${i}`}>
      <p className="font-fredoka font-bold text-blue-950">{tx.scene} {i + 1}</p>
      <div className="aspect-video rounded-2xl bg-sky-50 overflow-hidden flex items-center justify-center">
        {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <Wand2 className="w-8 h-8 text-sky-300" />}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 200))} rows={2} placeholder={tx.scenePh} className={studioField} data-testid={`scene-text-${i}`} />
      <button type="button" onClick={make} disabled={loading || !text.trim() || !idea.trim()} className="w-full bg-blue-600 text-white rounded-full py-2 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid={`scene-create-${i}`}>
        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {tx.sceneImg}
      </button>
    </div>
  );
};

const Choice = ({ icon: Icon, label, options, value, setValue, testId }) => (
  <div>
    <p className="font-bold text-blue-950 mb-2 inline-flex items-center gap-1"><Icon className="w-4 h-4" /> {label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => <button key={o} type="button" onClick={() => setValue(o)} className={`rounded-full px-4 py-2 font-bold text-sm border-4 ${value === o ? "border-blue-600 bg-sky-100" : "border-white bg-white"}`} data-testid={`${testId}-${o}`}>{o}</button>)}
    </div>
  </div>
);

export const VideoActivity = () => {
  const { lang } = useT();
  const tx = AI_TEXT[lang];
  const [idea, setIdea] = useState("");
  const [voice, setVoice] = useState(tx.voices[0]);
  const [music, setMusic] = useState(tx.musics[0]);
  return (
    <div className="space-y-4">
      <div>
        <p className="font-bold text-blue-950 mb-1">{tx.videoIdea}</p>
        <input value={idea} onChange={(e) => setIdea(e.target.value.slice(0, 150))} className={studioField} placeholder={lang === "es" ? "Ej: Lumi y Spark buscan una estrella perdida" : "E.g. Lumi and Spark look for a lost star"} data-testid="video-idea" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">{[0, 1, 2].map((i) => <SceneCard key={`${lang}${i}`} i={i} tx={tx} idea={idea} />)}</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Choice icon={Mic} label={tx.voice} options={tx.voices} value={voice} setValue={setVoice} testId="video-voice" />
        <Choice icon={Music} label={tx.music} options={tx.musics} value={music} setValue={setMusic} testId="video-music" />
      </div>
      <p className="bg-yellow-100 rounded-2xl px-4 py-3 font-semibold text-blue-950" data-testid="video-tip">{tx.videoTip}</p>
    </div>
  );
};

export const CoachActivity = () => {
  const { lang } = useT();
  const tx = AI_TEXT[lang];
  const { getToken } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const ask = async () => {
    setLoading(true);
    try {
      setReview((await makeApi(getToken).post("/ailab/coach", { prompt, language: lang })).data);
    } catch (e) {
      toast.error(aiErrorText(e, lang, tx.error));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        {EXAMPLES[lang].map(([bad, good]) => (
          <div key={bad} className="bg-white rounded-3xl p-4 shadow-md text-sm font-semibold">
            <p className="text-rose-600"><span className="font-bold">{tx.vague}:</span> "{bad}"</p>
            <p className="text-green-700 mt-1"><span className="font-bold">{tx.clear}</span> "{good}"</p>
          </div>
        ))}
      </div>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value.slice(0, 600))} rows={2} placeholder={tx.coachPh} className={studioField} data-testid="coach-input" />
      <button type="button" onClick={ask} disabled={loading || !prompt.trim()} className="bg-green-600 text-white rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 disabled:opacity-60" data-testid="coach-ask-btn">
        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} {tx.coachBtn}
      </button>
      {review && (
        <div className="bg-white rounded-3xl p-4 shadow-md flex gap-3" data-testid="coach-review">
          <img src="/img/spark.webp" alt="" className="w-14 h-auto self-start" />
          <div className="space-y-1">
            <div className="flex">{[1, 2, 3].map((n) => <Star key={n} className={`w-6 h-6 ${n <= review.stars ? "text-yellow-400 fill-yellow-400" : "text-slate-300"}`} />)}</div>
            <p className="font-semibold text-green-700">{review.good}</p>
            <p className="font-semibold text-blue-900">{review.tip}</p>
            <p className="font-bold text-blue-950 bg-sky-50 rounded-xl px-3 py-2">"{review.better}"</p>
            <button type="button" onClick={() => { setPrompt(review.better); setReview(null); }} className="text-sm font-bold text-blue-700 underline" data-testid="coach-use-better">{tx.useBetter}</button>
          </div>
        </div>
      )}
      {prompt.trim().length >= 3 && <Studio prompt={prompt} setPrompt={setPrompt} hidePrompt testId="coach-studio" />}
    </div>
  );
};
